import { useState } from 'react';
import type { Contractor } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';

interface ContractorPickerProps {
  contractors: Contractor[];
  value: string;
  onChange: (contractorId: string) => void;
  onContractorCreated: (contractor: Contractor) => void;
  /** Overrides the "+ New" inline form's name-field label and its Name-field placeholder context —
   * e.g. "Vendor" when this picker is used for Products instead of a trade Contractor. Purely
   * cosmetic: it still creates/reuses the same underlying Contractor entity either way. */
  label?: string;
}

/** A "— None —" + contractor dropdown with inline "+ New" creation — reused wherever something
 * needs to link to a Contractor, whether as a trade contractor (Estimates, Tasks) or a materials
 * vendor (Products) — both are the same underlying entity, see Product.contractorId. */
export function ContractorPicker({ contractors, value, onChange, onContractorCreated, label = 'contractor' }: ContractorPickerProps) {
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState('');
  const [trade, setTrade] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleAdd() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const created = await projectsApi.createContractor({
        name: name.trim(),
        ...(trade ? { trade } : {}),
        ...(phone ? { phone } : {}),
      });
      onContractorCreated(created);
      onChange(created.id);
      setShowNew(false);
      setName('');
      setTrade('');
      setPhone('');
    } finally {
      setSaving(false);
    }
  }

  if (showNew) {
    return (
      <div className="space-y-2 rounded-md border border-gray-200 p-3">
        <input
          type="text"
          placeholder="Name *"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        />
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            placeholder="Trade"
            value={trade}
            onChange={(e) => setTrade(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
          <input
            type="text"
            placeholder="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void handleAdd()}
            disabled={saving || !name.trim()}
            className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
          >
            {saving ? 'Adding…' : `Add ${label}`}
          </button>
          <button type="button" onClick={() => setShowNew(false)} className="text-sm text-gray-500 hover:underline">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
      >
        <option value="">— None —</option>
        {contractors.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
            {c.trade ? ` (${c.trade})` : ''}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => setShowNew(true)}
        className="shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
      >
        + New
      </button>
    </div>
  );
}
